import { AnimatePresence, motion } from 'motion/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { IconButton } from '@/components/ui/IconButton';
import { cn } from '@/lib/utils';

import { type ModalFC } from './modalsManager';

interface ImagePreviewModalProps {
  url: string;
  filename?: string;
  onDownload?: () => void;
  /** Keeps the image out of session recordings */
  masked?: boolean;
}

const ZOOM_STEP = 0.25;
const WHEEL_ZOOM_STEP = 0.05;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 5;
const ZOOM_DEFAULT = 1;
const ZOOM_TOGGLE = 2;
const ROTATE_STEP = 90;
/** Finger drift past this many px means a drag, not a tap */
const CLICK_SLOP_PX = 6;

interface TrackedPointer {
  x: number;
  y: number;
  startX: number;
  startY: number;
}

const clampZoom = (value: number): number =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));

const getMaxZoomForImage = (image: HTMLImageElement | null): number => {
  if (!image) return ZOOM_MAX;

  const { naturalWidth, naturalHeight, offsetWidth, offsetHeight } = image;
  if (
    naturalWidth <= 0 ||
    naturalHeight <= 0 ||
    offsetWidth <= 0 ||
    offsetHeight <= 0
  ) {
    return ZOOM_MAX;
  }

  const nativeScale = Math.max(
    naturalWidth / offsetWidth,
    naturalHeight / offsetHeight
  );

  return Math.max(ZOOM_TOGGLE, Math.min(ZOOM_MAX, nativeScale));
};

const clampZoomForImage = (
  value: number,
  image: HTMLImageElement | null
): number => Math.min(getMaxZoomForImage(image), clampZoom(value));

interface Offset {
  x: number;
  y: number;
}

interface TransformState {
  zoom: number;
  rotation: number;
  offset: Offset;
}

const clampOffset = (value: number, limit: number): number => {
  if (limit <= 0) return 0;
  return Math.min(limit, Math.max(-limit, value));
};

const getPanLimits = (
  image: HTMLImageElement,
  viewport: HTMLElement,
  zoom: number,
  rotation: number
): Offset => {
  const width = image.offsetWidth;
  const height = image.offsetHeight;

  if (width <= 0 || height <= 0) {
    return { x: 0, y: 0 };
  }

  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const transformedWidth =
    zoom * (Math.abs(width * cos) + Math.abs(height * sin));
  const transformedHeight =
    zoom * (Math.abs(width * sin) + Math.abs(height * cos));

  return {
    x: Math.max(0, (transformedWidth - viewport.clientWidth) / 2),
    y: Math.max(0, (transformedHeight - viewport.clientHeight) / 2),
  };
};

const clampOffsetToLimits = (offset: Offset, limits: Offset): Offset => ({
  x: clampOffset(offset.x, limits.x),
  y: clampOffset(offset.y, limits.y),
});

const isOffsetWithinLimits = (offset: Offset, limits: Offset): boolean =>
  Math.abs(offset.x) <= limits.x + 0.01 &&
  Math.abs(offset.y) <= limits.y + 0.01;

export const ImagePreviewModal: ModalFC<ImagePreviewModalProps> = ({
  open,
  onOpenChange,
  url,
  filename,
  onDownload,
  masked = false,
}) => {
  const [transform, setTransform] = useState<TransformState>({
    zoom: ZOOM_DEFAULT,
    rotation: 0,
    offset: { x: 0, y: 0 },
  });
  const [isGesturing, setIsGesturing] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const pointersRef = useRef(new Map<number, TrackedPointer>());
  // Set during a drag or pinch so the release click does not toggle or dismiss
  const dragMovedRef = useRef(false);
  const zoomGestureTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const dismiss = useCallback(() => onOpenChange(false), [onOpenChange]);
  const { zoom, rotation, offset } = transform;

  const markGesturing = useCallback(() => {
    setIsGesturing(true);
    if (zoomGestureTimeoutRef.current) {
      clearTimeout(zoomGestureTimeoutRef.current);
    }
    zoomGestureTimeoutRef.current = setTimeout(() => {
      setIsGesturing(false);
      zoomGestureTimeoutRef.current = null;
    }, 120);
  }, []);

  const stopGesturing = useCallback(() => {
    if (zoomGestureTimeoutRef.current) {
      clearTimeout(zoomGestureTimeoutRef.current);
      zoomGestureTimeoutRef.current = null;
    }
    setIsGesturing(false);
  }, []);

  const zoomAtPoint = useCallback(
    (
      resolveNextZoom: (currentZoom: number) => number,
      point: { clientX: number; clientY: number }
    ) => {
      const image = imageRef.current;
      const viewport = viewportRef.current;
      if (!image || !viewport) return;

      setTransform((current) => {
        const clampedZoom = clampZoomForImage(
          resolveNextZoom(current.zoom),
          image
        );
        if (clampedZoom === current.zoom) return current;

        const viewportRect = viewport.getBoundingClientRect();
        const cursorX =
          point.clientX - (viewportRect.left + viewportRect.width / 2);
        const cursorY =
          point.clientY - (viewportRect.top + viewportRect.height / 2);

        const desiredOffsetForZoom = (candidateZoom: number): Offset => {
          const ratio = candidateZoom / current.zoom;
          return {
            x: cursorX - ratio * (cursorX - current.offset.x),
            y: cursorY - ratio * (cursorY - current.offset.y),
          };
        };

        let resolvedZoom = clampedZoom;

        // If point-locking at target zoom would violate viewport bounds,
        // cap zoom to the highest feasible value that still keeps the anchor
        if (resolvedZoom > current.zoom) {
          const targetDesiredOffset = desiredOffsetForZoom(resolvedZoom);
          const targetLimits = getPanLimits(
            image,
            viewport,
            resolvedZoom,
            current.rotation
          );

          if (!isOffsetWithinLimits(targetDesiredOffset, targetLimits)) {
            let low = current.zoom;
            let high = resolvedZoom;

            for (let i = 0; i < 10; i += 1) {
              const mid = (low + high) / 2;
              const midDesiredOffset = desiredOffsetForZoom(mid);
              const midLimits = getPanLimits(
                image,
                viewport,
                mid,
                current.rotation
              );

              if (isOffsetWithinLimits(midDesiredOffset, midLimits)) {
                low = mid;
              } else {
                high = mid;
              }
            }

            resolvedZoom = low;

            // If strict point-locking is impossible at any higher zoom,
            // still allow a small zoom step instead of locking completely
            if (resolvedZoom <= current.zoom + 0.001) {
              resolvedZoom = Math.min(clampedZoom, current.zoom + ZOOM_STEP);
            }
          }
        }

        const desiredOffset = desiredOffsetForZoom(resolvedZoom);
        const limits = getPanLimits(
          image,
          viewport,
          resolvedZoom,
          current.rotation
        );

        return {
          ...current,
          zoom: resolvedZoom,
          offset: clampOffsetToLimits(desiredOffset, limits),
        };
      });
    },
    []
  );

  // Pan the image by a screen delta, clamped to the current zoom's bounds
  const panBy = useCallback((dx: number, dy: number) => {
    const image = imageRef.current;
    const viewport = viewportRef.current;
    if (!image || !viewport) return;

    setTransform((current) => {
      const limits = getPanLimits(
        image,
        viewport,
        current.zoom,
        current.rotation
      );
      return {
        ...current,
        offset: clampOffsetToLimits(
          { x: current.offset.x + dx, y: current.offset.y + dy },
          limits
        ),
      };
    });
  }, []);

  const applyCenteredZoom = useCallback(
    (resolveNextZoom: (currentZoom: number) => number) => {
      const image = imageRef.current;
      const viewport = viewportRef.current;

      setTransform((current) => {
        const clampedZoom = clampZoomForImage(
          resolveNextZoom(current.zoom),
          image
        );
        if (clampedZoom === current.zoom) return current;

        if (!image || !viewport) {
          return {
            ...current,
            zoom: clampedZoom,
            offset:
              clampedZoom === ZOOM_DEFAULT ? { x: 0, y: 0 } : current.offset,
          };
        }

        const limits = getPanLimits(
          image,
          viewport,
          clampedZoom,
          current.rotation
        );
        const centeredOffset =
          clampedZoom === ZOOM_DEFAULT
            ? { x: 0, y: 0 }
            : clampOffsetToLimits(current.offset, limits);

        return {
          ...current,
          zoom: clampedZoom,
          offset: centeredOffset,
        };
      });
    },
    []
  );

  const zoomIn = useCallback(() => {
    stopGesturing();
    applyCenteredZoom((currentZoom) => currentZoom + ZOOM_STEP);
  }, [applyCenteredZoom, stopGesturing]);

  const zoomOut = useCallback(() => {
    stopGesturing();
    applyCenteredZoom((currentZoom) => currentZoom - ZOOM_STEP);
  }, [applyCenteredZoom, stopGesturing]);

  const resetZoom = useCallback(() => {
    stopGesturing();
    setTransform({ zoom: ZOOM_DEFAULT, rotation: 0, offset: { x: 0, y: 0 } });
  }, [stopGesturing]);

  const rotate = useCallback(() => {
    stopGesturing();
    const image = imageRef.current;
    const viewport = viewportRef.current;

    setTransform((current) => {
      const nextRotation = current.rotation + ROTATE_STEP;

      if (!image || !viewport) {
        return { ...current, rotation: nextRotation };
      }

      const limits = getPanLimits(image, viewport, current.zoom, nextRotation);
      return {
        ...current,
        rotation: nextRotation,
        offset: clampOffsetToLimits(current.offset, limits),
      };
    });
  }, [stopGesturing]);

  const toggleZoom = useCallback(() => {
    stopGesturing();
    const image = imageRef.current;

    setTransform((current) => {
      const targetZoom =
        current.zoom === ZOOM_DEFAULT ? ZOOM_TOGGLE : ZOOM_DEFAULT;
      const nextZoom = clampZoomForImage(targetZoom, image);

      if (nextZoom === ZOOM_DEFAULT) {
        return { ...current, zoom: nextZoom, offset: { x: 0, y: 0 } };
      }

      return { ...current, zoom: nextZoom };
    });
  }, [stopGesturing]);

  const reconcileTransformToViewport = useCallback(() => {
    const image = imageRef.current;
    const viewport = viewportRef.current;
    if (!image || !viewport) return;

    setTransform((current) => {
      const clampedZoom = clampZoomForImage(current.zoom, image);
      const limits = getPanLimits(
        image,
        viewport,
        clampedZoom,
        current.rotation
      );
      const clampedOffset =
        clampedZoom === ZOOM_DEFAULT
          ? { x: 0, y: 0 }
          : clampOffsetToLimits(current.offset, limits);

      if (
        clampedZoom === current.zoom &&
        clampedOffset.x === current.offset.x &&
        clampedOffset.y === current.offset.y
      ) {
        return current;
      }

      return {
        ...current,
        zoom: clampedZoom,
        offset: clampedOffset,
      };
    });
  }, []);

  const isZoomed = zoom !== ZOOM_DEFAULT;

  const endPointer = (event: React.PointerEvent) => {
    pointersRef.current.delete(event.pointerId);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // A mouse has exactly one pointer, so any stored entry is from a press
    // whose pointerup landed outside the viewport; touch entries are safe
    // because touch pointers are captured implicitly to this element
    if (event.pointerType === 'mouse') pointersRef.current.clear();
    dragMovedRef.current = false;
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      startX: event.clientX,
      startY: event.clientY,
    });
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const pointers = pointersRef.current;
    const previous = pointers.get(event.pointerId);
    if (!previous) return;

    const moved = { x: event.clientX, y: event.clientY };
    const stored = { ...previous, x: moved.x, y: moved.y };
    pointers.set(event.pointerId, stored);

    if (
      Math.hypot(moved.x - previous.startX, moved.y - previous.startY) >
      CLICK_SLOP_PX
    ) {
      dragMovedRef.current = true;
    }

    if (pointers.size === 1) {
      // Skip at default zoom: the pan is a no-op there and would only
      // disable the transition
      if (isZoomed) {
        // Track the finger exactly instead of easing behind it
        markGesturing();
        panBy(moved.x - previous.x, moved.y - previous.y);
      }
    } else if (pointers.size === 2) {
      const other = [...pointers.entries()].find(
        ([id]) => id !== event.pointerId
      )?.[1];

      if (other) {
        const previousDistance = Math.hypot(
          previous.x - other.x,
          previous.y - other.y
        );
        const previousMidX = (previous.x + other.x) / 2;
        const previousMidY = (previous.y + other.y) / 2;

        const distance = Math.hypot(moved.x - other.x, moved.y - other.y);

        markGesturing();
        // Zoom anchored at the previous midpoint, then follow the midpoint
        if (previousDistance > 0 && distance > 0) {
          zoomAtPoint((zoom) => zoom * (distance / previousDistance), {
            clientX: previousMidX,
            clientY: previousMidY,
          });
        }
        panBy(
          (moved.x + other.x) / 2 - previousMidX,
          (moved.y + other.y) / 2 - previousMidY
        );
      }
    }
  };

  const handleCopyImage = useCallback(async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob }),
      ]);
      toast.success('Image copied');
    } catch {
      toast.error('Failed to copy image');
    }
  }, [url]);

  useEffect(
    () => () => {
      if (zoomGestureTimeoutRef.current) {
        clearTimeout(zoomGestureTimeoutRef.current);
        zoomGestureTimeoutRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (open) return;
    stopGesturing();
  }, [open, stopGesturing]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (!open) {
      document.documentElement.removeAttribute('data-image-preview-open');
      return;
    }

    document.documentElement.setAttribute('data-image-preview-open', 'true');

    return () => {
      document.documentElement.removeAttribute('data-image-preview-open');
    };
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      let handled = true;
      switch (e.key.toLowerCase()) {
        case 'escape':
          dismiss();
          break;
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
          zoomOut();
          break;
        case '0':
          resetZoom();
          break;
        case 'r':
          rotate();
          break;
        default:
          handled = false;
      }

      if (!handled) return;
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('keydown', handler, { capture: true });
    return () =>
      window.removeEventListener('keydown', handler, { capture: true });
  }, [open, dismiss, zoomIn, zoomOut, resetZoom, rotate]);

  // Mouse wheel zoom (point-anchored)
  useEffect(() => {
    if (!open) return;
    const handler = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        markGesturing();
        zoomAtPoint(
          (currentZoom) =>
            e.deltaY < 0
              ? currentZoom + WHEEL_ZOOM_STEP
              : currentZoom - WHEEL_ZOOM_STEP,
          e
        );
      }
    };
    window.addEventListener('wheel', handler, { passive: false });
    return () => window.removeEventListener('wheel', handler);
  }, [markGesturing, open, zoomAtPoint]);

  useEffect(() => {
    if (!open) return;

    reconcileTransformToViewport();
    window.addEventListener('resize', reconcileTransformToViewport);
    return () =>
      window.removeEventListener('resize', reconcileTransformToViewport);
  }, [open, reconcileTransformToViewport]);

  return (
    <AnimatePresence mode="wait">
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="fixed inset-0 z-50 flex flex-col bg-black/50 backdrop-blur-sm overflow-hidden"
          onClick={dismiss}
        >
          {/* Toolbar */}
          <div className="flex items-center justify-end gap-2 p-3 pr-6 pt-6">
            {/* Action group */}
            <ToolbarGroup>
              {isZoomed ? (
                <ToolbarIcon
                  icon={Icons.zoomOut}
                  onClick={toggleZoom}
                  tooltip="Zoom out"
                  tooltipOptions={{ hotkey: ['minus'] }}
                />
              ) : (
                <ToolbarIcon
                  icon={Icons.zoomIn}
                  onClick={toggleZoom}
                  tooltip="Zoom in"
                  tooltipOptions={{ hotkey: ['numpadadd'] }}
                />
              )}
              <ToolbarIcon
                icon={Icons.rotateCw}
                onClick={rotate}
                tooltip="Rotate"
                tooltipOptions={{ hotkey: ['r'] }}
              />
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <ToolbarIcon icon={Icons.moreHorizontal} />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={8}
                  className="w-52"
                >
                  {onDownload && (
                    <DropdownMenu.Item onClick={onDownload}>
                      <Icons.download className="size-3.5 mr-2" />
                      Download Image
                    </DropdownMenu.Item>
                  )}
                  <DropdownMenu.Item onClick={handleCopyImage}>
                    <Icons.copy className="size-3.5 mr-2" />
                    Copy Image
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </ToolbarGroup>

            {/* Close */}
            <ToolbarIcon
              icon={Icons.cancel}
              onClick={(e) => {
                e.stopPropagation();
                dismiss();
              }}
              tooltip="Close"
              tooltipOptions={{ hotkey: ['esc'], sideOffset: 3 }}
              size="md"
              className="border border-border-strong bg-surface rounded-lg smoothing-default"
            />
          </div>

          {/* Image area */}
          <div
            ref={viewportRef}
            className="flex-1 flex items-center justify-center overflow-hidden p-4 touch-none"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            onClick={(e) => {
              if (dragMovedRef.current) {
                dragMovedRef.current = false;
                // Keep the post-drag click from reaching the backdrop, which
                // would close the preview after every pan
                e.stopPropagation();
                return;
              }
              dismiss();
            }}
          >
            <img
              ref={imageRef}
              src={url}
              alt={filename ?? ''}
              className={cn(
                'max-w-[90vw] max-h-[85vh] object-contain select-none',
                isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in',
                masked && 'no-record ph-no-capture'
              )}
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg) scale(${zoom})`,
                transformOrigin: 'center center',
                transition: isGesturing ? 'none' : 'transform 0.2s ease',
              }}
              onClick={(e) => {
                e.stopPropagation();
                stopGesturing();
                if (dragMovedRef.current) {
                  dragMovedRef.current = false;
                  return;
                }
                if (isZoomed) {
                  toggleZoom();
                  return;
                }
                zoomAtPoint(() => ZOOM_TOGGLE, e);
              }}
              onLoad={reconcileTransformToViewport}
              draggable={false}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ToolbarIcon = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentProps<typeof IconButton>, 'variant'> & {
    size?: React.ComponentProps<typeof IconButton>['size'];
  }
>(({ className, size = 'sm', ...props }, ref) => (
  <IconButton
    ref={ref}
    variant="ghost"
    size={size}
    className={cn('text-foreground-subtle hover:text-foreground', className)}
    {...props}
    tooltipOptions={{
      ...props?.tooltipOptions,
      ...{ sideOffset: props?.tooltipOptions?.sideOffset ?? 7 },
    }}
  />
));
ToolbarIcon.displayName = 'ToolbarIcon';

function ToolbarGroup({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 border border-border-strong bg-surface rounded-lg smoothing-default px-[0.19rem] py-[0.19rem]',
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}
